/**
 * PURPOSE: Vista previa del ranking general del Cómputo General (FTR-0066) con lo ya guardado.
 * RESPONSIBILITIES: Top 10 por defecto con opción "Ver todo"; columnas Puesto (con "=" si hay empate), Institución,
 *   Puestos (puntos por puesto), Participación (+puntos) y Total; botón para volver a pedir el tablero.
 * COLLABORATORS: ComputoGeneralArea.jsx (tablero del backend), computoGeneral.utils.js.
 * ROUTE: winscore/src/area/ComputoGeneralArea/ComputoRankingPreview.jsx
 */
import React, { useState } from "react";
import { FaSyncAlt, FaTrophy } from "react-icons/fa";

import { formatLastUpdate } from "./computoGeneral.utils.js";

const TOP_ROWS = 10;

const ComputoRankingPreview = ({ ranking = [], lastUpdate, refreshing = false, onRefresh }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? ranking : ranking.slice(0, TOP_ROWS);
  const updated = formatLastUpdate(lastUpdate);

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <FaTrophy />
          </span>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Ranking general</h3>
            <p className="text-sm text-gray-500">
              Vista previa con lo guardado{updated ? ` · última edición: ${updated}` : ""}.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-200 px-3 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
            Actualizar
          </button>
          {ranking.length > TOP_ROWS ? (
            <button
              type="button"
              onClick={() => setShowAll((value) => !value)}
              className="inline-flex items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-bold text-blue-700 transition hover:bg-blue-100"
            >
              {showAll ? `Ver top ${TOP_ROWS}` : `Ver todo (${ranking.length})`}
            </button>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] text-left">
          <thead className="bg-gray-50 text-xs font-bold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="w-16 px-3 py-2 text-center">Puesto</th>
              <th className="px-3 py-2">Institución</th>
              <th className="w-20 px-3 py-2 text-center">Puestos</th>
              <th className="w-28 px-3 py-2 text-center">Participación</th>
              <th className="w-20 px-3 py-2 text-center">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.length ? (
              visible.map((row) => (
                <tr key={row.institution?.id ?? row.rank}>
                  <td className="px-3 py-2 text-center font-black text-gray-900" title={row.tied ? "Empatado" : undefined}>
                    {row.tied ? `=${row.rank}` : row.rank}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      {row.institution?.logo ? (
                        <img
                          src={row.institution.logo}
                          alt=""
                          className="h-7 w-7 shrink-0 rounded-full bg-white object-contain ring-1 ring-slate-200"
                        />
                      ) : null}
                      <div className="min-w-0">
                        <div className="text-sm font-black text-gray-900">{row.institution?.abbreviation || "—"}</div>
                        <div className="truncate text-xs text-gray-500" title={row.institution?.name}>
                          {row.institution?.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center font-bold text-gray-700">{row.totals?.placePoints ?? 0}</td>
                  <td className="px-3 py-2 text-center font-bold text-emerald-700">+{row.totals?.participationPoints ?? 0}</td>
                  <td className="px-3 py-2 text-center text-base font-black text-gray-900">{row.totals?.total ?? 0}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-sm font-semibold text-gray-500">
                  Todavía no hay instituciones en el cómputo de este evento.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComputoRankingPreview;
